import React, { useRef, useState, useEffect } from "react";
import { Paperclip, FileText, Image as ImageIcon, Mail, Download, Lock } from "lucide-react";
import { api } from "../lib/apiClient";
import { useAuth } from "../lib/AuthContext";
import { toast } from "./ui/sonner";

const KIND_ICON = { pdf: FileText, image: ImageIcon, msg: Mail, eml: Mail };

function fileSize(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * AttachmentsBadge — a small "📎 N" pill that opens a dropdown listing every
 * supplier PDF / image / .msg file attached to the current quote. Visible to
 * EVERY logged-in user, but actual download is gated by the per-user
 * `can_download_attachments` flag managed by admins in Users → Permissions.
 */
export function AttachmentsBadge({ attachments = [] }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const canDownload = user?.role === "admin" || user?.can_download_attachments !== false;

  useEffect(() => {
    if (!open) return;
    const onClickAway = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClickAway);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClickAway);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  if (!attachments.length) return null;

  const download = async (att) => {
    if (!canDownload) {
      toast.error("Your account is not permitted to download attachments. Ask an admin to enable this in Users → Permissions.");
      return;
    }
    try {
      const res = await api.get(`/files/${att.id}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = att.original_name || `${att.id}.pdf`;
      a.click();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not download file");
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="attachments-badge"
        className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full bg-white/15 hover:bg-white/25 text-white text-xs font-semibold transition-colors"
        title={canDownload
          ? `${attachments.length} attachment${attachments.length === 1 ? "" : "s"} — click to view & download`
          : `${attachments.length} attachment${attachments.length === 1 ? "" : "s"} — your account cannot download these (admin can enable)`}
      >
        <Paperclip size={12} className={canDownload ? "" : "opacity-60"} />
        <span>{attachments.length}</span>
        {!canDownload && <Lock size={10} className="opacity-70" data-testid="attachments-locked" />}
      </button>
      {open && (
        <div
          className="absolute z-30 top-full left-0 mt-2 w-80 max-h-96 overflow-auto rounded-lg bg-white border border-slate-200 shadow-2xl text-slate-900"
          data-testid="attachments-popover"
        >
          <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Attached files</span>
            <span className="text-[10px] text-slate-400">{attachments.length} total</span>
          </div>
          {!canDownload && (
            <div className="px-3 py-2 text-[11px] bg-amber-50 border-b border-amber-100 text-amber-800 flex items-start gap-2">
              <Lock size={12} className="mt-0.5 shrink-0" />
              <span>You can see the file list but downloads are disabled for your account. Ask an admin to enable <span className="font-semibold">Can download attachments</span> in Users → Permissions.</span>
            </div>
          )}
          <ul className="divide-y divide-slate-100">
            {attachments.map((a) => {
              const Icon = KIND_ICON[a.kind] || FileText;
              return (
                <li
                  key={a.id}
                  className={`px-3 py-2 flex items-center gap-3 transition-colors ${canDownload ? "hover:bg-slate-50 cursor-pointer" : "cursor-not-allowed opacity-70"}`}
                  onClick={() => canDownload && download(a)}
                  data-testid={`attachment-row-${a.id}`}
                >
                  <Icon size={16} className="text-slate-500 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium truncate" title={a.original_name}>{a.original_name}</div>
                    <div className="text-[10px] text-slate-500 flex items-center gap-2">
                      <span className="uppercase">{a.kind || "file"}</span>
                      {a.size > 0 && <span>· {fileSize(a.size)}</span>}
                      {a.scope && a.scope !== "general" && <span className="px-1.5 py-0.5 rounded bg-slate-100 text-[9px] uppercase">{a.scope}</span>}
                    </div>
                  </div>
                  {canDownload ? (
                    <Download size={14} className="text-slate-400 shrink-0" />
                  ) : (
                    <Lock size={12} className="text-slate-400 shrink-0" />
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
